import React, {ReactElement, ReactNode, useMemo} from 'react';
import cn from 'clsx';
import {CustomIcon, IconNames} from '@guestapp/ui';
import {
  BodyContainer,
  HeaderImg,
  HeaderText,
  HeaderWrapper,
  IconBadge,
  ItemContainer,
  SideBarContainer,
  Skeleton,
} from './styled';

export type SideBarProps = {
  children?: React.ReactElement[];
};

function SideBar({children}: SideBarProps) {
  return (
    <SideBarContainer className="sidebar">
      <Children>{children}</Children>
    </SideBarContainer>
  );
}

type HeaderProps = {
  children: React.ReactElement | string;
  show: boolean;
  onlyLogo?: boolean;
  imgSrc?: string;
  isLoading?: boolean;
};
const Header = ({children, imgSrc, show, isLoading, onlyLogo}: HeaderProps) => {
  if (!show) return null;
  return (
    <HeaderWrapper className="sidebar__header">
      {isLoading ? (
        <Skeleton className="sidebar__skeleton" />
      ) : (
        <>
          {imgSrc && (
            <HeaderImg
              src={imgSrc}
              height={48}
              $big={onlyLogo}
              className="sidebar__header-img"
            />
          )}
          {children && (
            <HeaderText $isImageHeader={Boolean(imgSrc)} className="sidebar__header-text">
              {children}
            </HeaderText>
          )}
        </>
      )}
    </HeaderWrapper>
  );
};

const Body = ({children}: {children: React.ReactElement[]}) => {
  return (
    <BodyContainer className="sidebar__body">
      <Children>{children}</Children>
    </BodyContainer>
  );
};

type ItemProps = {
  children: ReactNode;
  isActive: boolean;
  isLoading: boolean;
  iconName?: IconNames;
  onClick?: () => void;
  badgeData?: string;
  className?: string;
  customIcon?: ReactNode;
};
const Item = ({
  children,
  isActive,
  iconName,
  onClick,
  isLoading,
  badgeData,
  className,
  customIcon,
}: ItemProps) => {
  const icon = useMemo(() => {
    if (customIcon) {
      return customIcon;
    }
    if (iconName) {
      return <CustomIcon size={24} name={iconName} />;
    }
  }, [customIcon, iconName]);

  return (
    <ItemContainer
      onClick={onClick}
      className={cn('sidebar__item', className)}
      $isActive={isActive}
      $isLoading={isLoading}
    >
      {isLoading ? (
        <Skeleton className="sidebar__skeleton" />
      ) : (
        <>
          <div className="sidebar__icon">
            {icon}
            {badgeData && (
              <IconBadge className="sidebar__icon-badge">{badgeData}</IconBadge>
            )}
          </div>

          {children}
        </>
      )}
    </ItemContainer>
  );
};

type ContainerElementsProps = {
  children: ReactElement[];
  footer?: boolean;
};
const ContainerElements = ({children, footer}: ContainerElementsProps) => {
  const mainClassName = footer ? 'sidebar__footer' : 'sidebar__items-list';

  return (
    <div className={mainClassName}>
      <Children>{children}</Children>
    </div>
  );
};

const Children = ({children}: {children?: ReactElement[]}) => {
  if (children && children.length === 0) {
    return null;
  }
  return (
    <>
      {children &&
        React.Children.map(children, child => {
          return React.cloneElement(child);
        })}
    </>
  );
};

SideBar.Header = Header;
SideBar.Body = Body;
SideBar.Items = ContainerElements;
SideBar.Item = Item;
export {SideBar};
